import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "@/components/vansha/PublicNav";
import { OrbitSeed, CurveWeave, GrowthRings } from "@/components/vansha/Illustrations";
import { STAGES } from "@/components/vansha/StageIllustrations";
import { SunBloom } from "@/components/vansha/Graphics";
import { ArrowRight, Sparkles, Users, Heart, ShieldCheck } from "lucide-react";

const PILLARS = [
  { title: "Guidance",  body: "Week-by-week intelligence, gentle wisdom — for every chapter.",       Icon: Sparkles },
  { title: "Memory",    body: "Every photo, letter, voice — preserved in a private vault forever.", Icon: Heart },
  { title: "Family",    body: "One place for parents, grandparents, doctors — everyone aligned.",   Icon: Users },
  { title: "Care",      body: "Premium concierge, curated support, when you need it most.",         Icon: ShieldCheck },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-[#FBF4E8]" data-testid="landing-page">
      <PublicNav />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#F4A72C] mb-6" data-testid="hero-overline">The family operating system</p>
            <h1 className="font-serif font-semibold text-5xl sm:text-6xl lg:text-7xl leading-[1.05] text-[#1B1533]" data-testid="hero-title">
              From first<br />
              <span className="italic text-[#E8552B]">heartbeat</span> to<br />
              first steps.
            </h1>
            <p className="mt-8 text-lg text-[#3D2E5C] max-w-xl leading-relaxed" data-testid="hero-sub">
              A single, calm place for your family — through pregnancy, birth and the early years. Track, learn, remember and prepare together.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" className="bg-[#1B1533] text-white rounded-full px-8 py-4 font-medium hover:scale-105 transition-transform inline-flex items-center gap-2" data-testid="hero-cta-primary">
                Begin your journey <ArrowRight strokeWidth={1.5} size={16} />
              </Link>
              <Link to="/how-it-works" className="rounded-full px-8 py-4 font-medium border border-[#F5E7CE] hover:border-[#1B1533] transition" data-testid="hero-cta-secondary">
                See how it works
              </Link>
            </div>
            <div className="mt-12 flex items-center gap-6 text-xs uppercase tracking-[0.2em] text-[#7A6E96]">
              <span>Private by design</span><span className="w-1 h-1 rounded-full bg-ink-500" /><span>Family shared</span><span className="w-1 h-1 rounded-full bg-ink-500" /><span>Loved worldwide</span>
            </div>
          </motion.div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 0.2 }} className="lg:col-span-5 relative flex justify-center items-center">
            {/* Rich hero composite */}
            <div className="relative w-[500px] h-[500px] max-w-full">
              {/* soft blush disc */}
              <div className="absolute inset-6 rounded-full bg-[#FBE4D6]" />
              {/* sage disc */}
              <div className="absolute inset-24 rounded-full bg-[#7BA098]/25" />
              {/* orbit rings SVG */}
              <div className="absolute inset-0">
                <OrbitSeed size={500} />
              </div>
              {/* stage micro-graphics floating around */}
              <div className="absolute top-6 -left-2 bg-white rounded-2xl shadow-md p-3 rotate-[-6deg]" data-testid="hero-chip-week">
                <p className="text-[10px] uppercase tracking-widest text-[#F4A72C] font-bold">Week 24</p>
                <p className="font-serif text-2xl text-[#1B1533] leading-none mt-1">Corn.</p>
              </div>
              <div className="absolute bottom-8 -left-8 bg-[#1B1533] text-white rounded-2xl shadow-md p-3 rotate-[4deg]" data-testid="hero-chip-milestone">
                <p className="text-[10px] uppercase tracking-widest text-[#F4A72C] font-bold">Milestone</p>
                <p className="font-serif text-lg leading-none mt-1">First kick</p>
              </div>
              <div className="absolute top-16 -right-4 bg-white rounded-2xl shadow-md p-3 rotate-[6deg]" data-testid="hero-chip-letter">
                <p className="text-[10px] uppercase tracking-widest text-[#E8552B] font-bold">Letter · sealed</p>
                <p className="font-serif text-sm text-[#1B1533] leading-tight mt-1 max-w-[130px]">To open on your 1st birthday…</p>
              </div>
              <div className="absolute bottom-4 right-2 bg-[#F4A72C] text-[#1B1533] rounded-2xl shadow-md p-3 rotate-[-3deg]" data-testid="hero-chip-heartbeat">
                <p className="text-[10px] uppercase tracking-widest font-bold">Heartbeat</p>
                <p className="font-serif text-2xl leading-none mt-1">148 <span className="text-xs font-sans">bpm</span></p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pillars — Tetris asymmetric grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#F4A72C] mb-6">Four pillars</p>
            <h2 className="font-serif font-semibold text-4xl lg:text-5xl leading-tight text-[#1B1533]">Not another app.<br /><em className="text-[#E8552B]">An ecosystem.</em></h2>
            <p className="mt-6 text-[#3D2E5C]">Vansha is built on four quiet promises — every feature earns its place through one of them.</p>
          </div>
          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-6">
            {PILLARS.map((p, i) => (
              <motion.div key={p.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`bg-white border border-[#F5E7CE] rounded-3xl p-8 ${i === 0 ? "sm:mt-8" : ""} ${i === 3 ? "sm:mt-8" : ""}`} data-testid={`pillar-${p.title.toLowerCase()}`}>
                <div className={`w-14 h-14 rounded-2xl mb-6 flex items-center justify-center ${
                  i === 0 ? "bg-[#E8552B]/12 text-[#E8552B]" :
                  i === 1 ? "bg-[#F4A72C]/16 text-[#F4A72C]" :
                  i === 2 ? "bg-[#7BA098]/18 text-[#7BA098]" :
                            "bg-[#1B1533]/10 text-[#1B1533]"
                }`}>
                  <p.Icon strokeWidth={1.5} size={26} />
                </div>
                <h3 className="font-serif text-2xl mb-3">{p.title}</h3>
                <p className="text-[#3D2E5C]">{p.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Journey — Seven stage rich cards */}
      <section className="relative overflow-hidden bg-[#FBE4D6]/40 py-24 lg:py-32">
        <div className="absolute -right-24 -top-24 opacity-40 pointer-events-none">
          <SunBloom size={340} />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-16">
            <div className="lg:col-span-7">
              <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#F4A72C] mb-6">The Vansha journey</p>
              <h2 className="font-serif font-semibold text-4xl lg:text-6xl leading-[1.05] text-[#1B1533]">
                Seven stages.<br /><em className="text-[#E8552B]">One unbroken thread.</em>
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-lg text-[#3D2E5C] leading-relaxed">
                From the moment you begin dreaming — to the day your child steps into school. Every stage carries forward, so nothing is ever lost.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="stages-grid">
            {STAGES.map((s, i) => (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="bg-white border border-[#F5E7CE] rounded-3xl p-6 hover:-translate-y-1 hover:shadow-lg transition-transform group"
                data-testid={`stage-${s.key}`}
              >
                <div className="flex items-center justify-center mb-4">
                  <s.Illus size={160} />
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#F4A72C]">0{i + 1}</span>
                  <span className="h-px flex-1 bg-[#F5E7CE]" />
                </div>
                <h3 className="font-serif text-xl text-[#1B1533]">{s.name}</h3>
                <p className="text-sm text-[#7A6E96] mt-1">{s.sub}</p>
              </motion.div>
            ))}

            {/* Concluding tile — the unbroken thread */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 7 * 0.08, duration: 0.6 }}
              className="bg-[#1B1533] text-white rounded-3xl p-6 flex flex-col justify-between min-h-[260px]"
              data-testid="stage-thread-outro"
            >
              <div>
                <div className="w-12 h-12 rounded-2xl bg-[#E8552B]/20 flex items-center justify-center mb-4">
                  <ArrowRight strokeWidth={1.5} size={24} className="text-[#F4A72C]" />
                </div>
                <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#F4A72C] mb-2">Together</p>
                <h3 className="font-serif text-2xl leading-tight">One <em className="text-[#F4A72C]">unbroken</em> thread.</h3>
              </div>
              <Link to="/register" className="mt-6 inline-flex items-center gap-2 text-sm text-white group" data-testid="stages-cta">
                <span className="underline underline-offset-4">Start your journey</span>
                <ArrowRight strokeWidth={1.5} size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Manifesto strip */}
      <section className="bg-[#1B1533] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5"><CurveWeave size={400} /></div>
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#E8552B] mb-6">The takeaway</p>
            <blockquote className="font-serif font-semibold text-4xl lg:text-5xl leading-tight">
              &ldquo;We are not using an app.<br /><em className="text-[#F4A72C]">We have a companion</em> — helping us through one of the most important journeys of our lives.&rdquo;
            </blockquote>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-32">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5 flex justify-center"><GrowthRings size={320} /></div>
          <div className="lg:col-span-7">
            <h2 className="font-serif font-semibold text-4xl lg:text-5xl leading-tight">Your story deserves<br /><em className="text-[#E8552B]">to be remembered.</em></h2>
            <p className="mt-6 text-lg text-[#3D2E5C] max-w-xl">Begin free. Upgrade whenever the moment is right. Loved by families across the world.</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/register" className="bg-[#1B1533] text-white rounded-full px-8 py-4 font-medium hover:scale-105 transition-transform" data-testid="cta-register">Begin free</Link>
              <Link to="/pricing" className="rounded-full px-8 py-4 font-medium border border-[#F5E7CE] hover:border-[#1B1533] transition" data-testid="cta-pricing">See memberships</Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#F5E7CE] py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-[#7A6E96]" data-testid="footer">
          <span className="font-serif text-lg text-[#1B1533]">Vansha</span>
          <span>From first heartbeat to first steps · vansha.family</span>
          <div className="flex gap-6"><Link to="/story" className="hover:text-[#1B1533]">Story</Link><Link to="/features" className="hover:text-[#1B1533]">Features</Link><Link to="/pricing" className="hover:text-[#1B1533]">Pricing</Link></div>
        </div>
      </footer>
    </div>
  );
}
